import { useEffect, useId, useRef, type PropsWithChildren, type ReactNode } from 'react';

interface ModalProps extends PropsWithChildren {
  title: string;
  onClose: () => void;
  wide?: boolean;
  /** Extra-wide scrollable modal for long forms */
  xl?: boolean;
  /** When false, backdrop click does not close (Escape still works unless busy UI disables it). */
  closeOnBackdrop?: boolean;
  /** Optional controls rendered in the header before Close. */
  headerActions?: ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  title,
  onClose,
  wide = false,
  xl = false,
  closeOnBackdrop = true,
  headerActions,
  children,
}: ModalProps): JSX.Element {
  const sizeClass = xl ? ' modal-card--xl' : wide ? ' modal-card--wide' : '';
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    previousFocus.current = document.activeElement as HTMLElement | null;
    const card = cardRef.current;
    if (!card) return undefined;

    const focusables = () =>
      Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
      );

    const first = focusables()[0];
    (first ?? card).focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !cardRef.current) return;
      const nodes = focusables();
      if (nodes.length === 0) {
        event.preventDefault();
        return;
      }
      const firstNode = nodes[0];
      const lastNode = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === firstNode) {
        event.preventDefault();
        lastNode.focus();
      } else if (!event.shiftKey && document.activeElement === lastNode) {
        event.preventDefault();
        firstNode.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousFocus.current?.focus?.();
    };
  }, []);

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (closeOnBackdrop) onClose();
      }}
      role="presentation"
    >
      <div
        ref={cardRef}
        className={`modal-card${sizeClass}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="modal-card__header">
          <h2 id={titleId}>{title}</h2>
          <div className="modal-card__header-actions">
            {headerActions}
            <button className="ghost-button" type="button" onClick={onClose} aria-label="Close">
              Close
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
